'use client';
// The Product Updates panel. Feed it the "updates" array from POST /api/sdk/product-updates
// (fetched by your backend), and an onToggle that calls your backend, which calls
// PUT /api/sdk/product-updates/choices with the signed-in user's context.
// Styles: import './product-updates.css' once in your app.
import { useState, type ReactNode } from 'react';
import { UpdateToggle, type UpdateChangeHandler, type UpdateToggleHandler } from './UpdateToggle';

export type ProductUpdateBadge = 'devOnly' | 'preview' | 'beta' | 'new' | 'endOfLife';

export interface ProductUpdateItem {
  id: string;
  key: string;
  title: string;
  description: string;
  badge: ProductUpdateBadge | null;
  postDate: string;
  /** The main link, shown as a text link or a button. */
  link: { url: string; label: string; style: 'link' | 'button' } | null;
  /** A second, optional link (default text "Learn more"), also a text link or a button. */
  learnMore: { url: string; label: string; style: 'link' | 'button' } | null;
  toggle: { updateKey: string; enabled: boolean } | null;
}

const BADGE_LABELS: Record<ProductUpdateBadge, string> = {
  devOnly: 'Dev Only',
  preview: 'Preview',
  beta: 'Beta',
  new: 'New',
  endOfLife: 'End-of-Life',
};

export function ProductUpdates({
  updates,
  onToggle,
  onChange,
  onClose,
  onSuggest,
  title = 'Product Updates',
  suggestLabel = 'Submit a Product Suggestion',
  emptyText = 'Nothing new right now. Check back soon.',
  renderLink,
  showHeader = true,
}: {
  updates: ProductUpdateItem[];
  onToggle: UpdateToggleHandler;
  /** After a toggle saved: (updateKey, the saved value). Not called when the save fails. */
  onChange?: UpdateChangeHandler;
  onClose?: () => void;
  /** Footer button; omit to hide it. */
  onSuggest?: () => void;
  title?: string;
  suggestLabel?: string;
  emptyText?: string;
  /** Use your router's Link; defaults to a plain anchor. Called for the main link and for Learn more. */
  renderLink?: (link: { url: string; label: string; style: 'link' | 'button' }) => ReactNode;
  /** The enclosing drawer supplies its own title and close button. */
  showHeader?: boolean;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  return (
    <section className="bay-pu" aria-labelledby={showHeader ? 'bay-pu-title' : undefined} aria-label={showHeader ? undefined : title}>
      {showHeader && <header className="bay-pu-header">
        <h2 id="bay-pu-title" className="bay-pu-heading">{title}</h2>
        {onClose && (
          <button type="button" className="bay-pu-close" onClick={onClose} aria-label={`Close ${title}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        )}
      </header>}

      <div className="bay-pu-list">
        {updates.length === 0 && <p className="bay-pu-empty">{emptyText}</p>}
        {updates.map((u) => (
          <article key={u.id} className="bay-pu-item" aria-labelledby={`bay-pu-${u.id}`}>
            <div className="bay-pu-row">
              <h3 id={`bay-pu-${u.id}`} className="bay-pu-title">{u.title}</h3>
              {u.badge && <span className="bay-pu-badge" data-badge={u.badge}>{BADGE_LABELS[u.badge]}</span>}
            </div>
            <div className="bay-pu-row">
              <p className="bay-pu-desc">{u.description}</p>
              {u.toggle && (
                <UpdateToggle
                  updateKey={u.toggle.updateKey}
                  enabled={u.toggle.enabled}
                  onToggle={async (key, enabled) => {
                    setErrors(({ [u.id]: _, ...rest }) => rest);
                    return onToggle(key, enabled);
                  }}
                  onChange={onChange}
                  onError={(message) => setErrors((e) => ({ ...e, [u.id]: message }))}
                  label={u.title}
                />
              )}
            </div>
            {errors[u.id] && <p className="bay-pu-error" role="alert">{errors[u.id]}</p>}
            {(u.link || u.learnMore) && (
              <div className="bay-pu-links">
                {[u.link, u.learnMore].map((link) => link && (
                  <span key={link.url + link.label}>
                    {renderLink ? renderLink(link) : <a className={link.style === 'button' ? 'bay-pu-button' : 'bay-pu-link'} href={link.url}>{link.label}</a>}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>

      {onSuggest && (
        <footer className="bay-pu-footer">
          <button type="button" className="bay-pu-suggest" onClick={onSuggest}>{suggestLabel}</button>
        </footer>
      )}
    </section>
  );
}
